import { useState } from "react";
import { Ban, CheckCircle2, CopyPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Fatura, ResumoDeFatura, SimulacaoDeFechamento } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import {
  useBaixarFatura,
  useCancelarFatura,
  useFatura,
  useFecharCompetencia,
  useSimularFechamento,
} from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { PainelLateral } from "@/componentes/PainelLateral";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Botao } from "@/componentes/Botao";
import { Campo } from "@/componentes/Campo";
import { Pilula } from "@/componentes/PilulaDeSituacao";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { Confirmacao } from "@/componentes/Confirmacao";
import { useAvisos } from "@/componentes/Avisos";
import { useDetalheDaRota } from "@/componentes/useDetalheDaRota";
import { hojeUTC } from "@/calendario";
import { competencia, dataHora, dia, dinheiro, idCurto, quantidade } from "@/formato";
import { ROTA_DAS_FATURAS, rotaDaFatura } from "@/rotas";
import { franquia } from "@/telas/planos/plano";
import { ItensDaFatura } from "./ItensDaFatura";
import { aparenciaDaFatura, campoDaRecusa, nomeDoPagador, simulacaoDesatualizada } from "./fatura";

/*
 * O detalhe da fatura — itens, a cópia do plano, a baixa e o cancelamento.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A FATURA FECHADA SÓ MUDA DE SITUAÇÃO, E A TELA NÃO OFERECE OUTRA COISA.  │
 * │                                                                          │
 * │ `fechada → paga` e `fechada → cancelada`, e o banco recusa qualquer      │
 * │ outra (BM010). Por isso não há "editar" aqui: errou o valor, cancela e   │
 * │ gera a substituta, que referencia a cancelada. Os botões somem fora de   │
 * │ `fechada` — e se outra pessoa deu baixa nesse meio tempo, a recusa       │
 * │ `409 fatura_fora_da_situacao` aparece, e o detalhe recarrega.            │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function DetalheDaFatura({
  faturaID,
  resumo,
  aoFechar,
  aoAbrirOutra,
}: {
  faturaID: string;
  /** A linha da lista, para o cabeçalho não piscar enquanto o detalhe chega. */
  resumo?: ResumoDeFatura | undefined;
  aoFechar: () => void;
  /** Abre a substituta (ou a substituída) no lugar desta. */
  aoAbrirOutra: (id: string) => void;
}) {
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();
  const { data: fatura, error } = useFatura(cliente, faturaID);
  const cabecalho = fatura ?? resumo;

  const cancelar = useCancelarFatura(cliente, faturaID);
  const simular = useSimularFechamento(cliente);
  const fechar = useFecharCompetencia(cliente);

  const [baixando, setBaixando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [substituta, setSubstituta] = useState<SimulacaoDeFechamento | null>(null);

  const ehAdmin = pode("admin");

  function simularSubstituta() {
    simular.mutate(
      { substitui_fatura_id: faturaID },
      {
        onSuccess: setSubstituta,
        onError: (falha) => avisos.falhou(textoDoErro(falha)),
      },
    );
  }

  async function gerarSubstituta(marca: string) {
    try {
      const resultado = await fechar.mutateAsync({ substitui_fatura_id: faturaID, marca });
      setSubstituta(null);
      const gerada = resultado.faturas_geradas[0];
      if (gerada === undefined) {
        avisos.feito("Nenhuma substituta gerada", "O valor ficou zero.");
        return;
      }
      avisos.feito("Substituta gerada");
      aoAbrirOutra(gerada.id);
    } catch (falha) {
      // A marca nova remonta a confirmação com os números de agora.
      const nova = simulacaoDesatualizada(falha);
      if (nova !== null) {
        setSubstituta(nova);
        return;
      }
      throw falha;
    }
  }

  const rodape =
    !ehAdmin || fatura === undefined ? undefined : fatura.situacao === "fechada" ? (
      <div className="flex flex-wrap gap-2">
        <Botao onClick={() => setBaixando(true)}>
          <CheckCircle2 aria-hidden="true" className="size-4" />
          Registrar baixa
        </Botao>
        <Botao tom="destrutivo" onClick={() => setCancelando(true)}>
          <Ban aria-hidden="true" className="size-4" />
          Cancelar fatura
        </Botao>
      </div>
    ) : fatura.situacao === "cancelada" && fatura.substituida_por_id === null ? (
      <Botao tom="secundario" carregando={simular.isPending} onClick={simularSubstituta}>
        <CopyPlus aria-hidden="true" className="size-4" />
        Gerar substituta
      </Botao>
    ) : undefined;

  return (
    <>
      <PainelLateral
        aberto
        aoFechar={aoFechar}
        descricao={cabecalho ? `Fatura de ${nomeDoPagador(cabecalho)}` : "Fatura"}
        titulo={
          cabecalho === undefined ? (
            <span className="text-titulo font-semibold text-texto-2">Fatura</span>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="truncate text-titulo font-semibold text-texto">
                {nomeDoPagador(cabecalho)}
              </span>
              <span className="flex items-center gap-2">
                <span className="dados text-[12px] text-texto-2">
                  {competencia(cabecalho.competencia)} · {dinheiro(cabecalho.total_centavos)}
                </span>
                <Pilula matiz={aparenciaDaFatura(cabecalho).matiz}>
                  {aparenciaDaFatura(cabecalho).rotulo}
                </Pilula>
              </span>
            </div>
          )
        }
        {...(rodape !== undefined ? { rodape } : {})}
      >
        {error ? (
          <p role="alert" className="px-4 py-6 text-falha">
            {textoDoErro(error)}
          </p>
        ) : fatura === undefined ? (
          <div aria-hidden="true" className="m-4 h-40 rounded-cartao bg-superficie-2" />
        ) : (
          <CorpoDaFatura fatura={fatura} aoAbrirOutra={aoAbrirOutra} />
        )}
      </PainelLateral>

      {baixando && fatura !== undefined && (
        <BaixaDaFatura fatura={fatura} aoFechar={() => setBaixando(false)} />
      )}

      <Confirmacao
        pedido={
          cancelando && fatura !== undefined
            ? {
                severidade: "destrutiva",
                titulo: `Cancelar a fatura de ${competencia(fatura.competencia)}?`,
                corpo: "Sem substituta, a competência não é cobrada deste contrato.",
                verbo: "Cancelar fatura",
                exigeJustificativa: true,
              }
            : null
        }
        aoCancelar={() => setCancelando(false)}
        aoConfirmar={async (motivo) => {
          await cancelar.mutateAsync({ motivo });
          setCancelando(false);
          avisos.feito("Fatura cancelada");
        }}
      />

      <Confirmacao
        pedido={
          substituta === null || fatura === undefined
            ? null
            : {
                severidade: "leve",
                identidade: substituta.marca,
                titulo: `Gerar a substituta de ${competencia(fatura.competencia)}?`,
                corpo: (
                  <span className="dados text-texto">
                    {substituta.faturas === 0
                      ? "Valor zero: nenhuma fatura."
                      : `${substituta.faturas} fatura · ${dinheiro(substituta.total_centavos)}`}
                  </span>
                ),
                verbo: "Gerar substituta",
              }
        }
        aoCancelar={() => setSubstituta(null)}
        aoConfirmar={async () => {
          if (substituta !== null) await gerarSubstituta(substituta.marca);
        }}
      />
    </>
  );
}

/** O caminho das Fechadas: o link colado chega sem `?aba=`, e o fechar não muda de aba. */
function listaDasFechadas(busca: string): string {
  const consulta = new URLSearchParams(busca);
  consulta.set("aba", "fechadas");
  return `${ROTA_DAS_FATURAS}?${consulta.toString()}`;
}

/**
 * `/faturas/:id`: o painel sobre as Fechadas. O `key` remonta o detalhe ao trocar de fatura:
 * o estado de baixa ou de cancelamento de uma não pode vazar para a outra.
 */
export function FaturaDaRota() {
  const { id, linha, busca, fechar } = useDetalheDaRota<ResumoDeFatura>(listaDasFechadas);
  const navegar = useNavigate();

  return (
    <DetalheDaFatura
      key={id}
      faturaID={id}
      resumo={linha}
      aoFechar={fechar}
      aoAbrirOutra={(outra) => void navegar(rotaDaFatura(outra, busca))}
    />
  );
}

function CorpoDaFatura({
  fatura,
  aoAbrirOutra,
}: {
  fatura: Fatura;
  aoAbrirOutra: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="grid grid-cols-2 gap-2">
        {fatura.pessoa_nome !== null && (
          <CaixaDeCampo rotulo="Aplicação" className="col-span-2">
            {fatura.sistema_nome}
          </CaixaDeCampo>
        )}
        <CaixaDeCampo rotulo="Plano" className="col-span-2">
          {fatura.plano_nome}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Mensalidade" dados>
          {dinheiro(fatura.mensalidade_centavos)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Franquia" dados>
          {franquia(fatura.franquia)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Excedente" dados>
          {`${dinheiro(fatura.preco_excedente_centavos)} cada`}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Vigência" dados>
          {`${fatura.dias_vigentes}/${fatura.dias_na_competencia} dias`}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Na franquia" dados>
          {quantidade(fatura.emissoes_na_franquia)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Excedentes" dados>
          {quantidade(fatura.excedentes)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Vencimento" dados>
          {dia(fatura.vencimento)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Fechada" dados>
          {[dataHora(fatura.fechada_em), fatura.fechada_por_nome].filter(Boolean).join(" · ")}
        </CaixaDeCampo>

        {fatura.baixa !== null && (
          <>
            <CaixaDeCampo rotulo="Pago em" dados>
              {dia(fatura.baixa.pago_em)}
            </CaixaDeCampo>
            <CaixaDeCampo rotulo="Baixa por">{fatura.baixa.operador_nome}</CaixaDeCampo>
            {fatura.baixa.observacao !== null && (
              <CaixaDeCampo rotulo="Observação" className="col-span-2">
                {fatura.baixa.observacao}
              </CaixaDeCampo>
            )}
          </>
        )}

        {fatura.cancelamento !== null && (
          <>
            <CaixaDeCampo rotulo="Cancelada em" dados>
              {dataHora(fatura.cancelamento.cancelada_em)}
            </CaixaDeCampo>
            <CaixaDeCampo rotulo="Cancelada por">{fatura.cancelamento.operador_nome}</CaixaDeCampo>
            <CaixaDeCampo rotulo="Motivo" className="col-span-2">
              {fatura.cancelamento.motivo}
            </CaixaDeCampo>
          </>
        )}

        {fatura.substitui_fatura_id !== null && (
          <CaixaDeCampo rotulo="Substitui" dados>
            <BotaoDeFatura id={fatura.substitui_fatura_id} aoAbrir={aoAbrirOutra} />
          </CaixaDeCampo>
        )}
        {fatura.substituida_por_id !== null && (
          <CaixaDeCampo rotulo="Substituída por" dados>
            <BotaoDeFatura id={fatura.substituida_por_id} aoAbrir={aoAbrirOutra} />
          </CaixaDeCampo>
        )}
      </div>

      <section className="flex flex-col gap-2">
        <h3 className="rotulo">Itens</h3>
        <ItensDaFatura
          itens={fatura.itens}
          diasNaCompetencia={fatura.dias_na_competencia}
          totalCentavos={fatura.total_centavos}
        />
      </section>
    </div>
  );
}

function BotaoDeFatura({ id, aoAbrir }: { id: string; aoAbrir: (id: string) => void }) {
  return (
    <button
      type="button"
      title={id}
      onClick={() => aoAbrir(id)}
      className="text-esferografica underline-offset-2 hover:underline"
    >
      {idCurto(id)}
    </button>
  );
}

/*
 * A baixa manual: o dia do pagamento e uma observação, que vão para a fatura e
 * para a auditoria. O dia não passa de hoje (UTC) — a API recusa, e o `max` do
 * campo diz isso antes da viagem.
 */
function BaixaDaFatura({ fatura, aoFechar }: { fatura: Fatura; aoFechar: () => void }) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const baixar = useBaixarFatura(cliente, fatura.id);

  const hoje = hojeUTC();
  const [pagoEm, setPagoEm] = useState(hoje);
  const [observacao, setObservacao] = useState("");
  const [erroDaData, setErroDaData] = useState<string | undefined>(undefined);
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);

  function enviar() {
    setErroGeral(undefined);
    setErroDaData(undefined);
    if (pagoEm === "") {
      setErroDaData("Informe a data.");
      return;
    }

    baixar.mutate(
      { pago_em: pagoEm, ...(observacao.trim() !== "" && { observacao: observacao.trim() }) },
      {
        onSuccess: () => {
          avisos.feito("Baixa registrada");
          aoFechar();
        },
        onError: (falha) => {
          if (campoDaRecusa(falha) === "pago_em") setErroDaData(textoDoErro(falha));
          else setErroGeral(textoDoErro(falha));
        },
      },
    );
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Registrar baixa"
      descricao={`${nomeDoPagador(fatura)} · ${competencia(fatura.competencia)} · ${dinheiro(fatura.total_centavos)}`}
      aoEnviar={enviar}
      enviando={baixar.isPending}
      erro={erroGeral}
      rotuloDeEnvio="Registrar baixa"
    >
      <Campo
        rotulo="Data do pagamento"
        type="date"
        required
        max={hoje}
        value={pagoEm}
        onChange={(evento) => setPagoEm(evento.target.value)}
        erro={erroDaData}
        autoFocus
      />
      <Campo
        rotulo="Observação"
        maxLength={500}
        value={observacao}
        onChange={(evento) => setObservacao(evento.target.value)}
      />
    </ModalDeCadastro>
  );
}
